import AsyncStorage from '@react-native-async-storage/async-storage';
import { DeviceSettingsSchema, type DeviceSettings } from '../schemas/device-settings';

/**
 * Local-storage document helpers for device settings.
 *
 * Modeled on the check-in drafts blob (`@flavrly/...`): a single camelCase JSON
 * document read/parsed with defaults and merge-written. Lives inside `src/api`
 * (not `src/features`) so it can import the wire schema legally under the
 * data-layer seam rule and so the device query — which is also in `src/api` —
 * reaches it without an inverted api -> feature dependency.
 */

export const DEVICE_SETTINGS_STORAGE_KEY = '@flavrly/device-settings';

/**
 * Read the device settings document, resolving missing keys (or a corrupt/
 * absent blob) to their schema defaults.
 */
export async function readDeviceSettings(): Promise<DeviceSettings> {
  try {
    const raw = await AsyncStorage.getItem(DEVICE_SETTINGS_STORAGE_KEY);
    if (!raw) {
      return DeviceSettingsSchema.parse({});
    }
    return DeviceSettingsSchema.parse(JSON.parse(raw));
  } catch (error) {
    console.warn('Failed to read device settings, using defaults:', error);
    return DeviceSettingsSchema.parse({});
  }
}

/**
 * Merge a partial patch into the stored device settings document and return the
 * resulting full document. Immutable: reads, spreads onto a new object, writes.
 */
export async function writeDeviceSettings(
  patch: Partial<DeviceSettings>,
): Promise<DeviceSettings> {
  const current = await readDeviceSettings();
  const next = DeviceSettingsSchema.parse({ ...current, ...patch });
  await AsyncStorage.setItem(DEVICE_SETTINGS_STORAGE_KEY, JSON.stringify(next));
  return next;
}
